import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import { isAuthError } from "../../../shared";
import { profileService } from "../services";
import { severityOf } from "../services/serviceError";
import { useUser } from "./useUser";

/**
 * Inline edit state for the profile's name and email rows.
 *
 * One field is editable at a time, which is why this is a single hook rather
 * than one per row: opening a second editor has to close the first.
 *
 * Holds no domain rules - trimming and validation live in profileService, so
 * this is only React state plus user feedback.
 *
 * @param {{name: string, email: string}} current the values shown when not editing
 */
export const useProfileEditor = ({ name, email }) => {
  const { updateUser, clearAuth } = useUser();

  const [editingField, setEditingField] = useState(null);
  const [draftValue, setDraftValue] = useState("");
  const [saving, setSaving] = useState(false);

  const startEdit = useCallback(
    (field) => {
      setEditingField(field);
      setDraftValue(field === "name" ? name : email);
    },
    [name, email],
  );

  const cancelEdit = useCallback(() => {
    setEditingField(null);
    setDraftValue("");
  }, []);

  const saveEdit = useCallback(async () => {
    if (saving) return;

    // Checked synchronously so a rejected value never flashes "Saving...".
    try {
      profileService.validateProfileField(editingField, draftValue);
    } catch (error) {
      toast[severityOf(error)](error.message);
      return;
    }

    try {
      setSaving(true);

      const { patch, message } = await profileService.saveProfileField(
        editingField,
        draftValue,
      );

      updateUser(patch);
      toast.success(message);
      setEditingField(null);
      setDraftValue("");
    } catch (error) {
      console.error(error);

      if (isAuthError(error)) {
        toast.error("Session expired — please log in again.");
        clearAuth();
      } else {
        toast[severityOf(error)](
          error.userMessage || error.message || "An error occurred.",
        );
      }
    } finally {
      setSaving(false);
    }
  }, [saving, draftValue, editingField, updateUser, clearAuth]);

  return {
    editingField,
    draftValue,
    saving,
    setDraftValue,
    startEdit,
    saveEdit,
    cancelEdit,
  };
};
